/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
    TemplateRef,
    ViewContainerRef,
    ElementRef,
    ComponentRef,
    EmbeddedViewRef,
    Injector
} from '@angular/core';
import {
    throwNullPortalHostError,
    throwPortalAlreadyAttachedError,
    throwNoPortalAttachedError,
    throwNullPortalError,
    throwPortalHostAlreadyDisposedError,
    throwUnknownPortalTypeError
} from './portal-errors';


export interface ComponentType<T> {
  new (...args: any[]): T;
}
/**
 * A `Portal` is something that you want to render somewhere else.
 * It can be attach to / detached from a `PortalHost`.
 * @abstract
 */
export abstract class Portal<T> {
private _attachedHost: PortalHost | null;
/**
 * Attach this portal to a host.
 * @param {?} host
 * @return {?}
 */
attach(host: PortalHost): T {
    if (host == null) {
      throwNullPortalHostError();
    }

    if (host.hasAttached()) {
      throwPortalAlreadyAttachedError();
    }

    this._attachedHost = host;
    return /** @type {?} */(( <T> host.attach(this)));
  }
/**
 * Detach this portal from its host
 * @return {?}
 */
detach(): void {
    let /** @type {?} */ host = this._attachedHost;

    if (host == null) {
      throwNoPortalAttachedError();
    } else {
      this._attachedHost = null;
      host.detach();
    }
  }
/**
 * Whether this portal is attached to a host.
 * @return {?}
 */
get isAttached(): boolean {
    return this._attachedHost != null;
  }
/**
 * Sets the PortalHost reference without performing `attach()`. This is used directly by
 * the PortalHost when it is performing an `attach()` or `detach()`.
 * @param {?} host
 * @return {?}
 */
setAttachedHost(host: PortalHost | null) {
    this._attachedHost = host;
  }
}

function Portal_tsickle_Closure_declarations() {
/** @type {?} */
Portal.prototype._attachedHost;
}

/**
 * A `ComponentPortal` is a portal that instantiates some Component upon attachment.
 */
export class ComponentPortal<T> extends Portal<ComponentRef<T>> {
/**
 * The type of the component that will be instantiated for attachment.
 */
component: ComponentType<T>;
/**
 * [Optional] Where the attached component should live in Angular's *logical* component tree.
 * This is different from where the component *renders*, which is determined by the PortalHost.
 * The origin is necessary when the host is outside of the Angular application context.
 */
viewContainerRef?: ViewContainerRef | null;
/**
 * [Optional] Injector used for the instantiation of the component.
 */
injector?: Injector | null;
/**
 * @param {?} component
 * @param {?=} viewContainerRef
 * @param {?=} injector
 */
constructor(
      component: ComponentType<T>,
      viewContainerRef?: ViewContainerRef | null,
      injector?: Injector | null) {
    super();
    this.component = component;
    this.viewContainerRef = viewContainerRef;
    this.injector = injector;
  }
}

function ComponentPortal_tsickle_Closure_declarations() {
/**
 * The type of the component that will be instantiated for attachment.
 * @type {?}
 */
ComponentPortal.prototype.component;
/**
 * [Optional] Where the attached component should live in Angular's *logical* component tree.
 * This is different from where the component *renders*, which is determined by the PortalHost.
 * The origin is necessary when the host is outside of the Angular application context.
 * @type {?}
 */
ComponentPortal.prototype.viewContainerRef;
/**
 * [Optional] Injector used for the instantiation of the component.
 * @type {?}
 */
ComponentPortal.prototype.injector;
}

/**
 * A `TemplatePortal` is a portal that represents some embedded template (TemplateRef).
 */
export class TemplatePortal<C> extends Portal<C> {
/**
 * The embedded template that will be used to instantiate an embedded View in the host.
 */
templateRef: TemplateRef<C>;
/**
 * Reference to the ViewContainer into which the template will be stamped out.
 */
viewContainerRef: ViewContainerRef;

  context: C | undefined;
/**
 * @param {?} template
 * @param {?} viewContainerRef
 * @param {?=} context
 */
constructor(template: TemplateRef<any>, viewContainerRef: ViewContainerRef, context?: C) {
    super();
    this.templateRef = template;
    this.viewContainerRef = viewContainerRef;
    if (context) {
      this.context = context;
    }
  }
/**
 * @return {?}
 */
get origin(): ElementRef {
    return this.templateRef.elementRef;
  }
/**
 * Attach the the portal to the provided `PortalHost`.
 * When a context is provided it will override the `context` property of the `TemplatePortal`
 * instance.
 * @param {?} host
 * @param {?=} context
 * @return {?}
 */
attach(host: PortalHost, context: C | undefined = this.context): C {
    this.context = context;
    return super.attach(host);
  }
/**
 * @return {?}
 */
detach(): void {
    this.context = undefined;
    return super.detach();
  }
}

function TemplatePortal_tsickle_Closure_declarations() {
/**
 * The embedded template that will be used to instantiate an embedded View in the host.
 * @type {?}
 */
TemplatePortal.prototype.templateRef;
/**
 * Reference to the ViewContainer into which the template will be stamped out.
 * @type {?}
 */
TemplatePortal.prototype.viewContainerRef;
/** @type {?} */
TemplatePortal.prototype.context;
}



/**
 * A `PortalHost` is an space that can contain a single `Portal`.
 */
export interface PortalHost {
  attach(portal: Portal<any>): any;

  detach(): any;

  dispose(): void;

  hasAttached(): boolean;
}
/**
 * Partial implementation of PortalHost that only deals with attaching either a
 * ComponentPortal or a TemplatePortal.
 * @abstract
 */
export abstract class BasePortalHost implements PortalHost {
/**
 * The portal currently attached to the host.
 */
private _attachedPortal: Portal<any> | null;
/**
 * A function that will permanently dispose this host.
 */
private _disposeFn: (() => void) | null;
/**
 * Whether this host has already been permanently disposed.
 */
private _isDisposed: boolean = false;
/**
 * Whether this host has an attached portal.
 * @return {?}
 */
hasAttached(): boolean {
    return !!this._attachedPortal;
  }
/**
 * @param {?} portal
 * @return {?}
 */
attach(portal: Portal<any>): any {
    if (!portal) {
      throwNullPortalError();
    }

    if (this.hasAttached()) {
      throwPortalAlreadyAttachedError();
    }

    if (this._isDisposed) {
      throwPortalHostAlreadyDisposedError();
    }

    if (portal instanceof ComponentPortal) {
      this._attachedPortal = portal;
      return this.attachComponentPortal(portal);
    } else if (portal instanceof TemplatePortal) {
      this._attachedPortal = portal;
      return this.attachTemplatePortal(portal);
    }

    throwUnknownPortalTypeError();
  }
/**
 * @abstract
 * @template T
 * @param {?} portal
 * @return {?}
 */
attachComponentPortal<T>(portal: ComponentPortal<T>) {}
/**
 * @abstract
 * @template C
 * @param {?} portal
 * @return {?}
 */
attachTemplatePortal<C>(portal: TemplatePortal<C>) {}
/**
 * @return {?}
 */
detach(): void {
    if (this._attachedPortal) {
      this._attachedPortal.setAttachedHost(null);
      this._attachedPortal = null;
    }

    this._invokeDisposeFn();
  }
/**
 * @return {?}
 */
dispose() {
    if (this.hasAttached()) {
      this.detach();
    }

    this._invokeDisposeFn();
    this._isDisposed = true;
  }
/**
 * @param {?} fn
 * @return {?}
 */
setDisposeFn(fn: () => void) {
    this._disposeFn = fn;
  }
/**
 * @return {?}
 */
private _invokeDisposeFn() {
    if (this._disposeFn) {
      this._disposeFn();
      this._disposeFn = null;
    }
  }
}

function BasePortalHost_tsickle_Closure_declarations() {
/**
 * The portal currently attached to the host.
 * @type {?}
 */
BasePortalHost.prototype._attachedPortal;
/**
 * A function that will permanently dispose this host.
 * @type {?}
 */
BasePortalHost.prototype._disposeFn;
/**
 * Whether this host has already been permanently disposed.
 * @type {?}
 */
BasePortalHost.prototype._isDisposed;
}




// WEBPACK FOOTER //
// ../../../src/cdk/portal/portal.ts